/* eslint-disable no-console */
import {Button, Popconfirm, Table as TableAnt} from 'antd';
import {useWatch} from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import {NamePath, StoreValue} from 'antd/es/form/interface';
import moment from 'moment';
import React, {useContext, useEffect, useState} from 'react';
import ReactQuill from 'react-quill';
import {useSearchParams} from 'react-router-dom';

import {normalizeTableValue} from '../../pages/esgManager/constants/commonInputFormFunction';
import SubmitDisabledContext from '../../pages/esgManager/constants/SubmitDisabledContext';
import {inputType} from '../../pages/esgManager/interface/inputType';
import QuestionnaireSwitch from '../../pages/esgManager/utils/QuestionnaireSwitch';
import {replaceCurrentAndPrevious} from '../../shared/checkSelectedYear';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {formatIndian} from '../../utils/formatIndian';
import {Units} from '../Forms/Constants/CommonESGConstants/constants';
import {evaluateExpression} from '../Forms/Utils/expressionEvaluator';

import {Title} from './Constants/Common';
import ModalAddNewToTable from './Utils/ModalAddNewToTable';

const QuestionnaireTableWithFixedRow: React.FC<{
  field: any;
  getFieldValue: (name: NamePath) => StoreValue;
}> = ({field, getFieldValue}) => {
  // Compute fresh fixedRows every time dependencies change
  const isCustomFixedRow =
    field?.validationData?.[field.fieldName]?.fixedColumn?.length > 0;
  const computedFixedRows: any[] = isCustomFixedRow
    ? field?.validationData?.[field.fieldName]?.fixedColumn
    : field.fixedRows ?? [];

  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<any[]>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const stateOptions = useJSONStore((state) => state.states);
  const cityOptions = useJSONStore((state) => state.city);

  const [searchParams] = useSearchParams();
  const selectedYear = searchParams.get('selectedYear');

  const setIsSubmitDisabled = useContext(SubmitDisabledContext);

  const FI = useFormInstance();
  const {setFieldsValue} = FI;

  const dependentValue = field?.customFixedRowsBasedOnDependency
    ?.dependentFieldName
    ? useWatch(field?.customFixedRowsBasedOnDependency?.dependentFieldName, FI)
    : undefined;

  // Row fields that carry a pre-selected value for every fixed row
  const fieldsWithDefaults = (field?.formList?.formFields ?? []).filter(
    (formField: any) => formField?.defaultValue !== undefined
  );

  const buildRowDefaults = (rowId: string) =>
    fieldsWithDefaults.reduce((acc: any, formField: any) => {
      const dynamicFieldName = rowId
        ? `${formField.fieldName}_${rowId}`
        : formField.fieldName;
      acc[dynamicFieldName] = formField.defaultValue;

      return acc;
    }, {});

  useEffect(() => {
    const existing =
      normalizeTableValue(getFieldValue([field.fieldName])) ?? [];
    // If fixed rows are configured and there is no existing data, seed the table
    if ((computedFixedRows?.length || 0) > 0 && (existing?.length || 0) === 0) {
      const addedData = computedFixedRows?.map((row: any) => {
        const rowId = row?.rowId ?? row?.id;

        return {
          ...buildRowDefaults(rowId),
          ...row,
          _id: row?._id ?? row?.id,
          rowId: rowId,
          scopeLabel: row?.scopeLabel ?? row?.label
        };
      });
      setDataSource(addedData);
      setFieldsValue({[field.fieldName]: addedData});
    } else if (fieldsWithDefaults.length > 0 && (existing?.length || 0) > 0) {
      // Rows saved before a default was configured still need it filled in
      let changed = false;
      const withDefaults = existing.map((row: any) => {
        const defaults = buildRowDefaults(row?.rowId ?? row?.id);
        const missing = Object.keys(defaults).reduce((acc: any, key) => {
          if (row?.[key] === undefined || row?.[key] === null) {
            acc[key] = defaults[key];
          }

          return acc;
        }, {});

        if (Object.keys(missing).length === 0) {
          return row;
        }
        changed = true;

        return {...row, ...missing};
      });

      setDataSource(changed ? withDefaults : existing);
      if (changed) {
        setFieldsValue({[field.fieldName]: withDefaults});
      }
    } else {
      setDataSource(existing);
    }
  }, []);

  useEffect(() => {
    if (field?.customFixedRowsBasedOnDependency) {
      const sourceFieldValue = getFieldValue(
        field?.customFixedRowsBasedOnDependency?.dependentFieldName
      );

      const targetFieldValue = getFieldValue(field?.fieldName) ?? [];
      const fixedCustomRow =
        field?.customFixedRowsBasedOnDependency?.checkDependency(
          sourceFieldValue,
          targetFieldValue,
          computedFixedRows
        );

      const shouldUpdate =
        targetFieldValue.length !== fixedCustomRow.length ||
        (fixedCustomRow.length === 1 &&
          targetFieldValue[0]?.scopeLabel !== fixedCustomRow[0]?.scopeLabel);

      if (shouldUpdate) {
        setFieldsValue({[field.fieldName]: fixedCustomRow});
        setDataSource(fixedCustomRow);
      }
    }
  }, [dependentValue]);

  const handleDelete = (_id: string) => {
    const updatedDataSource = dataSource.filter((item) => item._id !== _id);
    setDataSource(updatedDataSource);
    setFieldsValue({[field.fieldName]: updatedDataSource});
    setIsSubmitDisabled(false);
  };

  const srNoColumn = [
    {
      title: 'S. No.',
      dataIndex: 'srNo',
      render: (id: any, record: any, index: any) => {
        return (page - 1) * pageSize + ++index;
      },
      width: 'fit-content'
    }
  ];

  const column = [
    {
      title: Title.actionTitle,
      dataIndex: 'operation',
      fixed: 'right',
      width: '100px',
      render: (value: any, record: any) => {
        return dataSource.length >= 1 ? (
          <>
            <Button
              onClick={() => {
                setShowModel(true);
                setEditData(record);
                setIsEdit(true);
                setEditId(record._id);
              }}
              type="link"
              style={{marginRight: '20px', padding: '4px 0px'}}
            >
              Edit
            </Button>
            {(computedFixedRows?.length || 0) === 0 && (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  handleDelete(record._id);
                }}
              >
                <Button type="link">Delete</Button>
              </Popconfirm>
            )}
          </>
        ) : null;
      }
    }
  ];

  const addedColumns = field?.formList?.formFields.map((field: any) => ({
    title: replaceCurrentAndPrevious(field.fieldLabel, selectedYear ?? ''),
    dataIndex: field.fieldName,
    render: (value: any, record: any) => {
      // For fixed rows, check if the record has scope-specific data
      const rowId = record?.rowId;
      const dynamicFieldName = rowId
        ? `${field.fieldName}_${rowId}`
        : field.fieldName;

      // Try to get the value from the dynamic field name first, then fallback to original field name
      const fieldValue =
        record[dynamicFieldName] !== undefined
          ? record[dynamicFieldName]
          : value;

      if (field.type === inputType.DATEPICKER) {
        return fieldValue
          ? moment(fieldValue).format(field?.dateFormat || 'DD-MM-YYYY')
          : '-';
      }
      if (field.type === inputType.CITY) {
        return fieldValue
          ? cityOptions.find((item) => item.value === fieldValue)?.label
          : '-';
      }
      if (field.type === inputType.STATE) {
        return fieldValue
          ? stateOptions.find((item) => item.value === fieldValue)?.label
          : '-';
      }
      if (field.type === inputType.PERCENTAGE) {
        return fieldValue ? `${fieldValue}${Units.percentage}` : '-';
      }
      if (field.type === inputType.NUMBER) {
        return fieldValue ? formatIndian(fieldValue) : '-';
      }
      if (field.type === inputType.WYSIWYG) {
        return fieldValue ? (
          <ReactQuill
            className="quill-table-cell"
            value={fieldValue}
            readOnly={true}
            theme={'bubble'}
          />
        ) : (
          '-'
        );
      }
      if (field.type === inputType.FILEUPLOAD) {
        if (!Array.isArray(value) || value.length === 0) {
          return '-';
        }

        return value.map((file: any) => (
          <div key={file.uid}>
            {file.url ? (
              <a href={file.url} target="_blank" rel="noreferrer">
                {file.name}
              </a>
            ) : (
              file.name
            )}
          </div>
        ));
      }

      if (Array.isArray(fieldValue)) {
        return fieldValue.length ? fieldValue.join(', ') : '-';
      }

      return fieldValue || '-';
    }
  }));

  const scopeColumn =
    Array.isArray(field?.scopeColumns) && field.scopeColumns.length > 0
      ? field.scopeColumns
      : (computedFixedRows?.length || 0) > 0
        ? [{title: field?.scopeColumnTitle, dataIndex: 'scopeLabel'}]
        : [];

  const columns = [...srNoColumn, ...scopeColumn, ...addedColumns, ...column];

  return (
    <>
      {(computedFixedRows?.length || 0) === 0 && (
        <Button
          onClick={() => {
            if (setIsEdit && setShowModel) {
              setShowModel(true);
              setIsEdit(false);
            }
          }}
          onKeyDown={(e) => {
            if (setIsEdit && setShowModel && e.key === 'Enter') {
              setShowModel(true);
              setIsEdit(false);
            }
          }}
          style={{float: 'right', marginBottom: '1rem'}}
          type="primary"
        >
          Add New
        </Button>
      )}
      <TableAnt
        className="questionnaire-table"
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'max-content'}}
        pagination={{
          pageSize,
          onChange(current) {
            setPage(current);
          }
        }}
      />

      <ModalAddNewToTable
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        field={field}
        data={isEdit ? editData : undefined}
        title={
          isEdit && editData?.scopeLabel
            ? `Edit ${editData.scopeLabel}`
            : undefined
        }
        FormComponent={field?.formList?.formFields.map(
          (formField: any, index: any) => {
            // Get the scope identifier from the current row being edited
            const rowId = isEdit && editData?.rowId ? editData.rowId : '';
            const dynamicFieldName = rowId
              ? `${formField.fieldName}_${rowId}`
              : formField.fieldName;

            let isDisabled = false;
            if (formField?.conditions?.shouldDisable) {
              isDisabled = evaluateExpression(
                formField?.conditions?.shouldDisable.expression,
                formField?.conditions?.shouldDisable?.formDataDependOn ===
                  'PARENT_FORM'
                  ? FI.getFieldValue([])
                  : getFieldValue([])
              );
            }

            return (
              <QuestionnaireSwitch
                field={{
                  fieldType: formField.type,
                  fieldLabel: replaceCurrentAndPrevious(
                    formField.fieldLabel,
                    selectedYear ?? ''
                  ),
                  textLimit: formField.textLimit,
                  options: formField.options,
                  heading: replaceCurrentAndPrevious(
                    formField.heading,
                    selectedYear ?? ''
                  ),
                  colSpan: formField.colSpan,
                  restrictFutureDateSelection:
                    formField.restrictFutureDateSelection,
                  fieldName: dynamicFieldName,
                  units: formField.units,
                  dateFormat: formField.dateFormat,
                  relatedKey: formField.relatedKey,
                  formula: formField.formula,
                  roundTo: formField.roundTo,
                  disabled: isDisabled,
                  rowId: rowId, // Pass rowId to field for condition evaluation
                  fileUploadAction: formField.fileUploadAction,
                  fileUploadValidExtensions:
                    formField.fileUploadValidExtensions,
                  fileUploadMaxSize: formField.fileUploadMaxSize,
                  fileUploadAllowMultipleFiles:
                    formField.fileUploadAllowMultipleFiles,
                  disabledOn: formField.disabledOn
                    ? {
                        ...formField.disabledOn,
                        fieldName: formField.disabledOn.fieldName
                          ? `${formField.disabledOn.fieldName}_${rowId}`
                          : formField.disabledOn.fieldName
                      }
                    : undefined,
                  requiredOn: formField.requiredOn
                    ? {
                        ...formField.requiredOn,
                        fieldName: formField.requiredOn.fieldName
                          ? `${formField.requiredOn.fieldName}_${rowId}`
                          : formField.requiredOn.fieldName
                      }
                    : undefined,
                  conditionalVisibility: formField.conditionalVisibility
                    ? {
                        ...formField.conditionalVisibility,
                        dependsOn: formField.conditionalVisibility.dependsOn
                          ? `${formField.conditionalVisibility.dependsOn}_${rowId}`
                          : formField.conditionalVisibility.dependsOn
                      }
                    : undefined
                }}
                key={index}
              />
            );
          }
        )}
        table={true}
        buttonText={'Save'}
        setDataSource={setDataSource}
        dataSource={dataSource}
        editId={editId}
        isEdit={isEdit}
      />
    </>
  );
};

export default QuestionnaireTableWithFixedRow;
